import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { setURLConfig, urlConfig } from './shared/constants/url-config';
import { Language, LanguageCode, LanguageFileName } from 'src/app/shared/Models/WidgetSettingsModels';
import { LayoutService } from './layout/layout.service';
import { ActivatedRoute } from '@angular/router';
import { GuestService } from './shared/services/guest.service';
import { LocalizeService } from './shared/services/localize.service';
import { DEFAULT_LANGUAGE_CODE } from './shared/constants/commonenums';
import { HttpService } from './shared/services/http.service';


@Injectable()
export class AppInitializer {

    configSettings: any;

    constructor(
        private httpClient: HttpClient,
        public translateService: TranslateService,
        public ls: LayoutService,
        private route: ActivatedRoute,
        private gs: GuestService,
        public localizeService: LocalizeService,
        private httpService: HttpService) { }

    public appConfigSettings() {
        return this.configSettings;
    }


    initializeApp(): Promise<any> {
        const isVersion2 = window.location.pathname;
        if (isVersion2.length && isVersion2.includes('/v2/')) {
            this.gs.isVersion2 = true;
        } else {
            this.gs.isVersion2 = false;
        }

        const configGet = new Promise<void>((resolve, reject) => {
            this.httpClient.get('assets/config/' + environment.configLocation).subscribe((response: any) => {
                this.configSettings = response;
                environment.BaseUrl = this.configSettings.BaseUrl + '/api/widget';
                environment.authorizeIframeResponseOrigin = this.configSettings.authorizeIframeResponseOrigin;
                environment.rGuestPayIframeResponsOrigin = this.configSettings.rGuestPayIframeResponsOrigin;
                environment.redirectToAdminPortal = this.configSettings.redirectToAdminPortal;
                setURLConfig();
                this.setMerchantCode();
                let feedbackURLSegements = window.location.pathname.split('/feedback/');
                if (this.gs.isVersion2) {
                    const url = new URL(window.location.href);
                    const language = url.searchParams.get('language');
                    this.localizeService.setLanguage(language ? language : Language[Number(Language.English)], 'universal', this.gs.restaurantMerchantCode)
                } else if (feedbackURLSegements?.length === 2) {
                    const propertyNameSegment = window.location.pathname.split("/feedback/")[1].split("/")[0];
                    const url = new URL(window.location.href);
                    const language = url.searchParams.get('language');
                    this.localizeService.setLanguage(language ? language : Language[Number(Language.English)], 'commentcard', `${propertyNameSegment}`)
                }
                this.httpClient.get(`assets/customerThemes/config/config_${this.gs.restaurantMerchantCode}.json`).subscribe((response: any) => {
                    this.configSettings.enableDining = response.enableDining;
                    this.configSettings.enableActivities = response.enableActivities;
                    this.processThemeService().then(() => resolve());
                }, (err) => {
                    this.configSettings.enableDining = true;
                    this.configSettings.enableActivities = true;
                    this.processThemeService().then(() => resolve());
                });

            });
        });

        return Promise.all([configGet]);
    }

    setMerchantCode() {
        if (this.gs.isVersion2) {

            this.gs.restaurantMerchantCode = this.getMerchantCode();
            sessionStorage.setItem('restaurantMerchantCode', this.gs.restaurantMerchantCode);
            this.processThemeService(); // to set client specific theme from external link
        }
    }


    getMerchantCode(): string {
        const browserUrl = window.location.href;
        let merchantCode = "";
        const pathList = browserUrl.split("/");
        if (browserUrl.includes('/activities/list/')) {
            let index = pathList.findIndex(path => path == 'list');
            merchantCode = pathList[index + 1];
        } else if (browserUrl.includes('/restaurants/')) {
            let index = pathList.findIndex(path => path == 'restaurants');
            merchantCode = pathList[index + 1];
        }
        else if (browserUrl.includes('/feedback/')) {
            let index = pathList.findIndex(path => path == 'feedback');
            merchantCode = pathList[index + 1];
        } 
        else {
            merchantCode = browserUrl.includes("confirmation") ? pathList[pathList.length - 2] : pathList[pathList.length - 1];
        }
        [merchantCode] = merchantCode.split("?");
        return merchantCode;
    }



    processThemeService(): Promise<void> {
        return new Promise<void>((resolve) => {
            const merchantCode = this.gs.restaurantMerchantCode || this.getMerchantCode();
            if (!merchantCode) {
                resolve();
                return;
            }

            const apiUrl = `${urlConfig.getCustomStyleSheet}?code=${encodeURIComponent(merchantCode)}`;
            const remoteThemeUrl = this.configSettings?.styleURL ? `${this.configSettings.styleURL}/styles_${merchantCode}.css` : null;

            const loadFallbackTheme = () => {
                if (!remoteThemeUrl) {
                    resolve();
                    return;
                }

                this.httpClient.get(remoteThemeUrl, { responseType: 'text' }).subscribe(
                    (val: any) => {
                        this.injectStylesheet(val, 'appthemes');
                        resolve();
                    },
                    (err2) => {
                        console.error('Failed to load theme from fallback:', err2);
                        resolve();
                    }
                );
            };

            if (!environment.fetchStylesLiteralsFromBlob) {
                loadFallbackTheme();
                return;
            }

            this.httpService.get(apiUrl).subscribe(
                (response) => {
                    if (response?.Payload?.downloadUrl) {
                        this.httpService.get(response.Payload.downloadUrl, true).subscribe(
                            (cssContent: string) => {
                                this.injectStylesheet(cssContent, 'styles_azure');
                                resolve();
                            },
                            (err) => {
                                console.error('Failed to download custom stylesheet from API downloadUrl, falling back:', err);
                                loadFallbackTheme();
                            }
                        );
                    } else {
                        loadFallbackTheme();
                    }
                },
                (err) => {
                    console.error('Failed to fetch custom stylesheet URL from API, falling back:', err);
                    loadFallbackTheme();
                }
            );
        });
    }

    injectStylesheet(css, id) {
        const head = document.head || document.getElementsByTagName('head')[0];
        const styleTag: any = document.createElement('style');
        styleTag.type = 'text/css';
        styleTag.id = id;
        styleTag.innerHTML = css;
        head.appendChild(styleTag);
    }

    public defaultLocale(): string {
        this.setLanguage();
        let localeValue = sessionStorage.getItem('languageCode');
        if (localeValue == LanguageCode.chinesesimplified) {
            return "zh-cn"
        }else if (localeValue == LanguageCode.chinesetraditional) {
            return "zh-tw";
        }
        return sessionStorage.getItem('languageCode') || DEFAULT_LANGUAGE_CODE;
    }

    setLanguage(){
        let url = new URL(window.location.href);

        const language = getLanguageFromRouteParam(url) || getLanguageFromQueryParam(url); 

        if(language){
            sessionStorage.setItem('languageCode', LanguageCode[language]);
        }
    }

    
}


export function getLanguageFromQueryParam(url){

    let params = new URLSearchParams(url.search);

    return params.get('language')
}

export function getLanguageFromRouteParam(url){

    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length === 4 && segments[0].match('orders') && segments[1] && segments[2] && segments[3]){
        return segments[3].toLowerCase();
    }
    return null;
}
